import * as React from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid2,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from "@mui/material";
import styled from "styled-components";
import type {
    Index,
    IndexCategoryConfig,
    IndexConfigResponse,
    IndexSubCategoryConfig,
} from "src/scenes/indexes-settings/indexes-types";
import { LoadingButton } from "@mui/lab";
import cloneDeep from "lodash-es/cloneDeep";
import isEqual from "lodash-es/isEqual";
import request from "src/utils/request";
import IndexCategoriesToggle from "src/components/indexes-settings/index-categories-toggle";
import { useParams } from "react-router-dom";
import { useAuth } from "src/components/providers/auth-context";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useTeams } from "src/scenes/teams/teams-provider";
import IndexSubCategoriesToggle from "src/components/indexes-settings/index-subcategories-toggle";
import SingleIndexSettings from "./single-index";

const H1 = styled.h1`
    font-size: 22px;
    font-weight: bold;
    color: ${({ theme }) => theme.palette.primary.main};
    text-transform: capitalize;
`;

const H2 = styled.h2`
    font-size: 14px;
    font-weight: bold;
    margin: 0;
    color: ${({ theme }) => theme.palette.primary.main};
    text-transform: capitalize;
`;

const IndexesSettings = () => {
    const { teamId } = useParams();
    const { isDemo } = useAuth();

    const baseUrl = teamId ? `/api/indexes/${teamId}` : `/api/indexes`;
    const [indexDefinitions, setIndexDefinitions] = React.useState<
        IndexConfigResponse["definitions"]
    >([]);
    const [cleanDefinitions, setCleanDefinitions] = React.useState<
        IndexConfigResponse["definitions"]
    >([]);
    const [defaultDefinitions, setDefaultDefinitions] = React.useState<
        IndexConfigResponse["definitions"]
    >([]);
    const [isOptedIn, setIsOptedIn] = React.useState<boolean>(false);
    const [isDirty, setIsDirty] = React.useState<boolean>(false);
    const [isDefault, setIsDefault] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [selectedCategory, setSelectedCategory] = React.useState<string>("work");
    const [selectedSubCategory, setSelectedSubCategory] = React.useState<string | null>(null);
    const [showAdvanced, setShowAdvanced] = React.useState<boolean>(false);
    const teamName = () => {
        const teamsApi = useTeams();
        if (teamsApi && teamsApi.data) {
            const teamData = teamsApi.data.find((team) => team.id === teamId);
            return `for ${teamData.name}`;
        }
        return " ";
    };
    const editIndexDefinition =
        (categoryIndex: number, subcategoryIndex: number, indexIndex: number) =>
        (indexConfig: Index) => {
            setIndexDefinitions((prevState) => {
                const category = prevState[categoryIndex];
                if (!category) return prevState;
                const subcategory = category.subcategories[subcategoryIndex];
                if (!subcategory) return prevState;
                subcategory.indexes.splice(indexIndex, 1);
                subcategory.indexes.splice(indexIndex, 0, indexConfig);
                return [...prevState];
            });
        };

    const editSubCategoryDefinition = (
        categoryIndex: number,
        subcategoryIndex: number,
        subcategory: IndexSubCategoryConfig
    ) => {
        setIndexDefinitions((prevState) => {
            const category = prevState[categoryIndex];
            if (!category) return prevState;
            category.subcategories.splice(subcategoryIndex, 1);
            category.subcategories.splice(subcategoryIndex, 0, subcategory);
            return [...prevState];
        });
    };

    const editCategoryDefinition = (categoryIndex: number, category: IndexCategoryConfig) => {
        setIndexDefinitions((prevState) => {
            prevState.splice(categoryIndex, 1);
            prevState.splice(categoryIndex, 0, category);
            return [...prevState];
        });
    };

    const getIndexesConfig = async () => {
        let response: IndexConfigResponse;
        try {
            if (isDemo) {
                // @ts-expect-error expected
                response = (await import("./mock-data")).default as IndexConfigResponse;
            } else {
                response = await request<IndexConfigResponse>(`${baseUrl}/config`);
            }
            setIndexDefinitions(response?.definitions);
            setCleanDefinitions(cloneDeep(response?.definitions));
            setDefaultDefinitions(cloneDeep(response?.defaultDefinitions));
            setIsOptedIn(response?.optedIn);
        } catch (_e) {
            // do nothing
        }
    };

    React.useEffect(() => {
        getIndexesConfig();
    }, []);

    React.useEffect(() => {
        if (isEqual(indexDefinitions, cleanDefinitions)) {
            setIsDirty(false);
        } else {
            setIsDirty(true);
        }
    }, [indexDefinitions]);

    React.useEffect(() => {
        if (isEqual(indexDefinitions, defaultDefinitions)) {
            setIsDefault(true);
        } else {
            setIsDefault(false);
        }
    }, [indexDefinitions]);

    const toggleOpt = async () => {
        try {
            setLoading(true);
            await request(`${baseUrl}`, {
                method: "PUT",
                body: JSON.stringify({ on: !isOptedIn }),
            });
            setIsOptedIn(!isOptedIn);
        } finally {
            setLoading(false);
        }
    };

    const saveSetup = async () => {
        try {
            setLoading(true);
            await request(`${baseUrl}/config`, {
                method: "POST",
                body: JSON.stringify(indexDefinitions),
            });
            setCleanDefinitions(cloneDeep(indexDefinitions));
            setIsDirty(false);
        } finally {
            setLoading(false);
        }
    };

    const discardChanges = () => {
        setIndexDefinitions(cloneDeep(cleanDefinitions));
        setIsDirty(false);
    };

    const resetToDefault = () => {
        setIndexDefinitions(cloneDeep(defaultDefinitions));
        setIsDirty(false);
    };

    return (
        <Box mt={4} mb={5} width="100%">
            <Stack spacing={3} direction="column" alignItems="flex-start" justifyContent="center">
                <Stack
                    direction="column"
                    width="100%"
                    style={{
                        width: "100%",
                        position: "sticky",
                        top: 60,
                        zIndex: 1000,
                        backgroundColor: "#303030",
                        padding: "12px",
                        boxShadow: "0 5px 6px rgba(48, 48, 48, 1)",
                    }}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center">
                            <H1>Indexes Settings</H1>
                            {teamId && (
                                <p style={{ margin: 0, paddingLeft: "8px", fontWeight: "200" }}>
                                    {teamName()}
                                </p>
                            )}
                        </Stack>
                        <Grid2 container direction="row">
                            <Grid2>
                                <Button onClick={() => discardChanges()}>
                                    {isDirty ? "Discard" : "Cancel"}
                                </Button>
                            </Grid2>
                            <Grid2>
                                <LoadingButton
                                    disabled={!isDirty}
                                    loading={loading}
                                    onClick={() => saveSetup()}
                                >
                                    Save Setup {isOptedIn ? "" : "and Enable"}
                                </LoadingButton>
                            </Grid2>
                            <Grid2>
                                <LoadingButton loading={loading} onClick={() => toggleOpt()}>
                                    {isOptedIn ? "Opt-out" : "Opt-in"}
                                </LoadingButton>
                            </Grid2>
                        </Grid2>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                        <IndexCategoriesToggle
                            selectedCategory={selectedCategory}
                            setSelectedCategory={setSelectedCategory}
                        />
                        <FormControlLabel
                            label={<span style={{ fontSize: "12px" }}>Advanced Settings</span>}
                            control={
                                <Checkbox
                                    checked={showAdvanced}
                                    size="small"
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        setShowAdvanced(event.target.checked);
                                    }}
                                />
                            }
                        />
                        {!isDefault && (
                            <Button
                                onClick={() => resetToDefault()}
                                size="small"
                                startIcon={<RestartAltIcon />}
                            >
                                Reset to defaults
                            </Button>
                        )}
                    </Stack>
                </Stack>
                {indexDefinitions
                    .filter((category) => category.name === selectedCategory)
                    .map((category, categoryIndex) => (
                        <Grid2 container spacing={2} width="100%" key={category.name}>
                            <Grid2 size={12}>
                                <Stack width="100%" spacing={2}>
                                    <H2>{category.name}</H2>
                                    <FormControlLabel
                                        label={
                                            <span style={{ fontSize: "12px" }}>
                                                {category.name.charAt(0).toUpperCase() +
                                                    category.name.slice(1).toLowerCase()}{" "}
                                                {category.enabled ? "Enabled" : "Disabled"}
                                            </span>
                                        }
                                        control={
                                            <Checkbox
                                                checked={category.enabled}
                                                size="small"
                                                onChange={(
                                                    event: React.ChangeEvent<HTMLInputElement>
                                                ) => {
                                                    editCategoryDefinition(categoryIndex, {
                                                        ...category,
                                                        enabled: event.target.checked,
                                                    });
                                                }}
                                            />
                                        }
                                    />
                                    {!category.enabled && (
                                        <Stack
                                            style={{ width: "100%", opacity: "0.7" }}
                                            alignItems="center"
                                        >
                                            <VisibilityOffIcon />
                                            <p style={{ margin: "0" }}>
                                                {category.name
                                                    .split(" ")
                                                    .map(
                                                        (word) =>
                                                            word.charAt(0).toUpperCase() +
                                                            word.slice(1).toLowerCase()
                                                    )
                                                    .join(" ")}{" "}
                                                indexes have been disabled.
                                            </p>
                                        </Stack>
                                    )}
                                    {category.enabled && (
                                        <>
                                            <FormControl size="small">
                                                <InputLabel
                                                    style={{ fontSize: "12px" }}
                                                    id="aggregation-cat"
                                                >
                                                    Sub-Category Aggregation Method
                                                </InputLabel>
                                                <Select
                                                    labelId="aggregation-cat"
                                                    value={category.aggregation}
                                                    label="Sub-Category Aggregation Method"
                                                    size="small"
                                                    onChange={(event) => {
                                                        editCategoryDefinition(categoryIndex, {
                                                            ...category,
                                                            aggregation: event.target.value as
                                                                | "sum"
                                                                | "avg",
                                                        });
                                                    }}
                                                >
                                                    <MenuItem value="sum">Sum</MenuItem>
                                                    <MenuItem value="avg">
                                                        Weighted Average
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                            <TextField
                                                label="Weight"
                                                type="number"
                                                inputProps={{
                                                    step: 0.1,
                                                }}
                                                size="small"
                                                value={category.weight_coefficient}
                                                onChange={(
                                                    event: React.ChangeEvent<HTMLInputElement>
                                                ) => {
                                                    editCategoryDefinition(categoryIndex, {
                                                        ...category,
                                                        weight_coefficient: parseFloat(
                                                            event.target.value
                                                        ),
                                                    });
                                                }}
                                            />
                                        </>
                                    )}
                                </Stack>
                            </Grid2>
                            {category.enabled && (
                                <>
                                    <IndexSubCategoriesToggle
                                        selectedSubCategory={selectedSubCategory}
                                        setSelectedSubCategory={setSelectedSubCategory}
                                        subcategories={category.subcategories}
                                    />

                                    {category.subcategories
                                        .filter(
                                            (subcategory) => subcategory.id === selectedSubCategory
                                        )
                                        .map((subcategory, subcategoryIndex) => (
                                            <Grid2 size={12} key={subcategory.name}>
                                                <Stack width="100%" spacing={2}>
                                                    <H2>{subcategory.name}</H2>
                                                    <FormControlLabel
                                                        label={
                                                            <span style={{ fontSize: "12px" }}>
                                                                {subcategory.name
                                                                    .split(" ")
                                                                    .map(
                                                                        (word) =>
                                                                            word
                                                                                .charAt(0)
                                                                                .toUpperCase() +
                                                                            word
                                                                                .slice(1)
                                                                                .toLowerCase()
                                                                    )
                                                                    .join(" ")}{" "}
                                                                {subcategory.enabled
                                                                    ? "Enabled"
                                                                    : "Disabled"}
                                                            </span>
                                                        }
                                                        control={
                                                            <Checkbox
                                                                checked={subcategory.enabled}
                                                                size="small"
                                                                onChange={(
                                                                    event: React.ChangeEvent<HTMLInputElement>
                                                                ) => {
                                                                    editSubCategoryDefinition(
                                                                        categoryIndex,
                                                                        subcategoryIndex,
                                                                        {
                                                                            ...subcategory,
                                                                            enabled:
                                                                                event.target
                                                                                    .checked,
                                                                        }
                                                                    );
                                                                }}
                                                            />
                                                        }
                                                    />
                                                    {subcategory.enabled && (
                                                        <>
                                                            <FormControl size="small">
                                                                <InputLabel
                                                                    style={{ fontSize: "12px" }}
                                                                    id="aggregation-sub"
                                                                >
                                                                    Sub-Metrics Aggregation Method
                                                                </InputLabel>
                                                                <Select
                                                                    labelId="aggregation-sub"
                                                                    value={subcategory.aggregation}
                                                                    label="Sub-Metrics Aggregation Method"
                                                                    size="small"
                                                                    onChange={(event) => {
                                                                        editSubCategoryDefinition(
                                                                            categoryIndex,
                                                                            subcategoryIndex,
                                                                            {
                                                                                ...subcategory,
                                                                                aggregation: event
                                                                                    .target
                                                                                    .value as
                                                                                    | "sum"
                                                                                    | "avg",
                                                                            }
                                                                        );
                                                                    }}
                                                                >
                                                                    <MenuItem value="sum">
                                                                        Sum
                                                                    </MenuItem>
                                                                    <MenuItem value="avg">
                                                                        Weighted Average
                                                                    </MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                            <FormControl size="small">
                                                                <InputLabel
                                                                    style={{ fontSize: "12px" }}
                                                                    id="bonus-sub"
                                                                >
                                                                    Days-off Bonus
                                                                </InputLabel>
                                                                <Select
                                                                    labelId="bonus-sub"
                                                                    value={
                                                                        subcategory.days_off_bonus
                                                                    }
                                                                    label="Days-off Bonus"
                                                                    size="small"
                                                                    onChange={(event) => {
                                                                        editSubCategoryDefinition(
                                                                            categoryIndex,
                                                                            subcategoryIndex,
                                                                            {
                                                                                ...subcategory,
                                                                                days_off_bonus:
                                                                                    event.target
                                                                                        .value as
                                                                                        | "positive"
                                                                                        | "negative"
                                                                                        | "none",
                                                                            }
                                                                        );
                                                                    }}
                                                                >
                                                                    <MenuItem value="positive">
                                                                        Positive
                                                                    </MenuItem>
                                                                    <MenuItem value="negative">
                                                                        Negative
                                                                    </MenuItem>
                                                                    <MenuItem value="none">
                                                                        No Bonus
                                                                    </MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                            {category.aggregation !== "sum" && (
                                                                <TextField
                                                                    label="Weight"
                                                                    type="number"
                                                                    size="small"
                                                                    inputProps={{
                                                                        step: 0.1,
                                                                    }}
                                                                    value={
                                                                        subcategory.weight_coefficient
                                                                    }
                                                                    onChange={(
                                                                        event: React.ChangeEvent<HTMLInputElement>
                                                                    ) => {
                                                                        editSubCategoryDefinition(
                                                                            categoryIndex,
                                                                            subcategoryIndex,
                                                                            {
                                                                                ...subcategory,
                                                                                weight_coefficient:
                                                                                    parseFloat(
                                                                                        event.target
                                                                                            .value
                                                                                    ),
                                                                            }
                                                                        );
                                                                    }}
                                                                />
                                                            )}
                                                        </>
                                                    )}
                                                </Stack>
                                                {subcategory.enabled &&
                                                    subcategory.indexes.map((index, indexIndex) => (
                                                        <Grid2
                                                            size={12}
                                                            mt={1}
                                                            mb={1}
                                                            key={index.descriptive_name}
                                                        >
                                                            <SingleIndexSettings
                                                                index={index}
                                                                showAdvanced={showAdvanced}
                                                                subcategory={subcategory}
                                                                editIndex={editIndexDefinition(
                                                                    categoryIndex,
                                                                    category.subcategories.findIndex(
                                                                        (s) =>
                                                                            s.id ===
                                                                            selectedSubCategory
                                                                    ),
                                                                    indexIndex
                                                                )}
                                                            />
                                                        </Grid2>
                                                    ))}
                                            </Grid2>
                                        ))}
                                </>
                            )}
                        </Grid2>
                    ))}
            </Stack>
        </Box>
    );
};

export default IndexesSettings;
